import { useCallback, useContext } from 'react';
import { DeviceDetailContext } from 'app/agriculture/routes/Devices/DeviceDetails/context/DeviceDetailContextProvider';
import { RoundedContentSection } from 'app/agriculture/components/RoundedContentBox/RoundedContentSection';
import { InlineButton } from 'app/agriculture/components/Button/Button';
import {
    useTimelineDispatch,
    useTimelineViewMode,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import { mailToHandler } from 'app/agriculture/components/MailTo/mailTo';
import { ConfigService } from 'app/config/config.service';
import { InlineTimestamp } from 'app/agriculture/components/Timestamp/InlineTimestamp';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { brandConfig } from 'app/agriculture/queries/snapshots/config/brandConfig';

export function DeviceOfflineContent() {
    const { setInterval } = useTimelineDispatch();
    const { getNewVisibleInterval } = useTimelineViewMode();

    const now = useThrottledNow();
    const { device } = useContext(DeviceDetailContext);
    const latestDataSnapshot = useSnapshot({
        parentId: device.id,
        snapshotType: brandConfig[device.brand].offlineLatestSnapshotType,
        shelfLife: null,
        cursorTimeOverride: now,
    });

    const { timestamp } = latestDataSnapshot ?? {};

    const handleTimelineClick = useCallback(() => {
        if (!timestamp) return;
        setInterval(getNewVisibleInterval(timestamp), { cursorTime: timestamp });
    }, [timestamp, setInterval, getNewVisibleInterval]);

    const handleMailToClick = mailToHandler({
        email: ConfigService.defaultHelpEmail,
        subject: `Assistance with device ${device.name} - ${device.id}`,
        body: 'Hi the above device is currently offline. [Insert more detail here]',
    });

    if (!timestamp) {
        return (
            <RoundedContentSection heading="Offline device information">
                <h5>No recorded data</h5>
                <p>The system has not received any data from this device.</p>
            </RoundedContentSection>
        );
    }

    return (
        <RoundedContentSection heading="Offline device information">
            <h5>Last contact</h5>
            <p>
                <InlineTimestamp timestamp={timestamp} />
                {' - for more diagnostics view the '}
                <InlineButton
                    text="timeline"
                    onClick={handleTimelineClick}
                    id="last-contact-timeline"
                />
                .
            </p>
            <p>
                {'If the problem persists '}
                <InlineButton
                    id="device-get-help-mailto"
                    text="contact support"
                    onClick={handleMailToClick}
                    tooltip={`mailto: ${ConfigService.defaultHelpEmail}`}
                />
                .
            </p>
        </RoundedContentSection>
    );
}
